// S-066-8 / DEC-066（DEC-065 追補）真因(a): カレンダーペースト鮮度の可視化。
//
// calendar_days/{YYYY-MM-DD} は手動コピペで、実カレンダーと同期せず腐る（6/25 焼付き事故の
// 真因(a)＝凍結スナップショット腐敗）。本モジュールは保存時刻 `updatedAt` から「いつ保存したか」を
// 相対表示し、古いペースト（腐ったスナップショット）の無音消費にユーザーが気づける導線を作る。
//
// 本タスクは「表示のみ」: 自動削除・自動再取込はしない（DEC-066 必達）。時間帯マッチ（S-066-6）の
// 前提でも依存でもない独立の防御レイヤ。S-066-6 が真因(c) を解いても (a) は生存するため明示分離する。
//
// トーン規約（DEC-009）: 精度・%・confidence・「マッチ成功率」を一切出さない。相対時間（N日前）と
// 中立な注意文のみ。古さは「可能性」止まりで断定しない。
//
// 純関数のみ（React/Firestore 依存なし）＝単体テスト容易。Firestore Timestamp は構造的に受ける
// （`.toDate()` / `.seconds` を持つ）ため firebase import 不要で、テストでモックを作りやすい。

// 何日以上前の保存を「古い可能性」として注意表示するか（暦日差・JST）。
export const STALE_PASTE_DAYS = 7;

// 古いペーストへの注意文（DEC-009 中立トーン・精度/%/件数なし・断定しない）。
export const STALE_PASTE_NOTICE =
  '予定表が古い可能性があります。最新の予定に貼り替えるか、相手先をご確認ください。';

// Firestore Timestamp / Date / {seconds} / epoch ms / ISO 文字列 を Date へ正規化。読めなければ null。
//   serverTimestamp() はサーバ ack 前のローカルスナップショットで null になりうる → null を返し非表示にする。
export function toDateSafe(v: unknown): Date | null {
  if (v == null) return null;
  // Firestore Timestamp（本番経路）。
  if (typeof (v as { toDate?: unknown }).toDate === 'function') {
    try {
      const d = (v as { toDate: () => Date }).toDate();
      return Number.isNaN(d.getTime()) ? null : d;
    } catch {
      return null;
    }
  }
  if (v instanceof Date) return Number.isNaN(v.getTime()) ? null : v;
  // {seconds, nanoseconds} 形（Timestamp 互換オブジェクト）。
  if (typeof (v as { seconds?: unknown }).seconds === 'number') {
    const d = new Date((v as { seconds: number }).seconds * 1000);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  if (typeof v === 'number') {
    const d = new Date(v);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  if (typeof v === 'string') {
    const ms = Date.parse(v);
    return Number.isNaN(ms) ? null : new Date(ms);
  }
  return null;
}

// JST 壁時計の暦日インデックス（1970-01-01 JST = 0）。TZ 非依存（+9h シフトして 86400000 で割る）。
function jstDayIndex(d: Date): number {
  return Math.floor((d.getTime() + 9 * 3600 * 1000) / 86400000);
}

// updatedAt（保存時刻）から now までの JST 暦日差。未解決なら null。未来（時計ズレ/serverTimestamp pending）は 0 にクランプ。
export function daysSincePaste(updatedAt: unknown, now: Date = new Date()): number | null {
  const saved = toDateSafe(updatedAt);
  if (!saved) return null;
  const diff = jstDayIndex(now) - jstDayIndex(saved);
  return diff < 0 ? 0 : diff;
}

export interface PasteFreshness {
  days: number; // 経過暦日数（>=0）
  label: string; // '今日更新' / '昨日更新' / 'N日前に更新'
  stale: boolean; // STALE_PASTE_DAYS 以上前＝古い可能性
}

// 鮮度ラベル＋古さフラグ。updatedAt 未解決なら null（呼出側で何も表示しない＝fail-soft）。
export function pasteFreshness(updatedAt: unknown, now: Date = new Date()): PasteFreshness | null {
  const days = daysSincePaste(updatedAt, now);
  if (days == null) return null;
  let label: string;
  if (days <= 0) label = '今日更新';
  else if (days === 1) label = '昨日更新';
  else label = `${days}日前に更新`;
  return { days, label, stale: days >= STALE_PASTE_DAYS };
}
